import { useEffect, useState } from "react";
import { toast } from "sonner";
import { authFetch } from "../App";
import { Brain, Loader2 } from "lucide-react";

/**
 * App-Setting: „Nina im Menü anzeigen".
 *
 * App-weite Einstellung. Nur Admins dürfen sie ändern (Backend erzwingt das
 * ebenfalls). Der Menüpunkt wird zusätzlich durch `permissions.nina_access`
 * pro User geschützt — die Backend-Route `/app/nina` bleibt unabhängig davon
 * über den Nina-Engine-Guard gesichert.
 */
export default function NinaMenuSettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    let alive = true;
    authFetch("/api/nina-engine/settings/menu-visibility")
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!alive) return;
        if (d && typeof d.enabled_for_admins === "boolean") setEnabled(d.enabled_for_admins);
      })
      .catch(() => {})
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const handleToggle = async () => {
    const next = !enabled;
    setEnabled(next); // optimistic
    setSaving(true);
    try {
      const res = await authFetch("/api/nina-engine/settings/menu-visibility", {
        method: "PUT",
        body: JSON.stringify({ enabled_for_admins: next }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = await res.json();
      setEnabled(!!data.enabled_for_admins);
      toast.success(next ? "Nina im Menü aktiviert." : "Nina im Menü ausgeblendet.");
    } catch (e) {
      setEnabled(!next); // rollback
      toast.error(`Konnte nicht speichern: ${e.message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className="rounded-xl border p-4"
      style={{ backgroundColor: "var(--tga-bg-card)", borderColor: "var(--tga-border)" }}
      data-testid="nina-menu-settings"
    >
      <div className="flex items-start gap-3">
        <div
          className="p-2 rounded-lg"
          style={{ backgroundColor: "rgba(217, 70, 239, 0.12)", color: "rgb(217, 70, 239)" }}
        >
          <Brain className="w-4 h-4" />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="text-sm font-semibold" style={{ color: "var(--tga-text-primary)" }}>
            Nina im Menü anzeigen
          </h2>
          <p className="text-xs mt-1" style={{ color: "var(--tga-text-muted)" }}>
            Wenn aktiv, sehen alle Admins mit Nina-Zugriff einen Menüpunkt „Nina“ (Desktop im Bereich
            „System“, Mobile im Menü „Mehr“). Der eigentliche Zugriff auf Nina bleibt unabhängig
            davon über <code className="text-[10px]">permissions.nina_access</code> geschützt.
          </p>
          <div className="mt-3 flex items-center gap-3">
            <button
              type="button"
              role="switch"
              aria-checked={enabled}
              onClick={handleToggle}
              disabled={loading || saving}
              className="relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50"
              style={{
                backgroundColor: enabled ? "rgb(217, 70, 239)" : "rgba(148, 163, 184, 0.4)",
              }}
              data-testid="nina-menu-toggle"
            >
              <span
                className="inline-block h-5 w-5 transform rounded-full bg-white transition-transform"
                style={{ transform: enabled ? "translateX(22px)" : "translateX(2px)" }}
              />
            </button>
            <span className="text-xs" style={{ color: "var(--tga-text-primary)" }} data-testid="nina-menu-state">
              {loading ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Lade …
                </span>
              ) : saving ? (
                <span className="inline-flex items-center gap-1">
                  <Loader2 className="w-3 h-3 animate-spin" /> Speichere …
                </span>
              ) : enabled ? (
                "Menüpunkt sichtbar für berechtigte Admins"
              ) : (
                "Menüpunkt komplett ausgeblendet"
              )}
            </span>
          </div>
        </div>
      </div>
    </section>
  );
}
